import { useTranslation } from 'react-i18next'
import {
  UseAddRolePopupProps,
  UseAddRolePopupReturn,
} from '../../utils/types/roles/RolesTypes'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/Store'
import {
  useAddMasterRoleMutation,
  useAddRoleMutation,
  useLazyGetAppTypesWithRolesQuery,
  useUpdateRoleMutation,
} from '../../api/app/UserManagementApiService'
import { useEffect, useState } from 'react'
import { showToast } from '../../components/common/toast/Toast'
import {
  resetCapabilityDetails,
  resetRoleAssignmentMapping,
  resetRoleDetails,
} from '../../redux/reducers/rolesManagementReducer'

export const useAddRolePopup = ({
  onCloseModal,
  isMaster,
}: UseAddRolePopupProps): UseAddRolePopupReturn => {
  const { t } = useTranslation(['translation'])
  const dispatch = useDispatch()

  const roleDetails = useSelector(
    (state: RootState) => state.roleCapabilities.roleDetails,
  )
  const capabilitiesFinalResult = useSelector(
    (state: RootState) => state.roleCapabilities.capabilityDetails,
  )
  const roleAssignmentMappingFinal = useSelector(
    (state: RootState) => state.roleCapabilities.roleAssignmentMapping,
  )

  const [getAppTypesWithRoleList, appTypesWithRoles] =
    useLazyGetAppTypesWithRolesQuery()

  const [activeStep, setActiveStep] = useState(0)
  const [addRole] = useAddRoleMutation()
  const [updateRole] = useUpdateRoleMutation()
  const [addMasterRole] = useAddMasterRoleMutation()

  const steps = [
    { label: t('common.basic_details') },
    { label: t('user_management.select_capabilities') },
  ]

  useEffect(() => {
    getAppTypesWithRoleList(isMaster)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster])

  const displayErrorToast = (message: string, code: number) => {
    showToast(`${message}(${code})`, 'error')
  }

  const handleCloseModel = () => {
    dispatch(resetRoleDetails())
    dispatch(resetCapabilityDetails())
    dispatch(resetRoleAssignmentMapping())
    onCloseModal(false)
  }

  const backButtonHandler = () => {
    setActiveStep((prev) => prev - 1)
  }

  const continueButtonHandler = () => {
    setActiveStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev))
  }

  const addEditRoleHandler = async () => {
    const filteredSubElements = capabilitiesFinalResult?.map((item) => {
      const features = item?.features
      const res = features?.filter((feature) => feature?.capabilities?.length)
      return { ...item, features: res }
    })

    const filteredRootElements = filteredSubElements?.filter(
      (item) => item?.features?.length,
    )

    if (!filteredRootElements?.length) {
      showToast(
        t('user_management.role_cannot_be_created_without_any_capability'),
        'error',
      )
      return
    }

    const rolePayload = {
      roleName: roleDetails?.roleName,
      roleDescription: roleDetails?.roleDescription,
      applicationTypeCode: roleDetails?.applicationTypeCode,
      featureGroup: filteredRootElements,
      roleAssignmentMapping: roleAssignmentMappingFinal || [],
    }

    try {
      const response = roleDetails?.isEdit
        ? await updateRole({
            ...rolePayload,
            roleCode: roleDetails?.role,
          }).unwrap()
        : isMaster
        ? await addMasterRole(rolePayload).unwrap()
        : await addRole(rolePayload).unwrap()

      if (response?.requestStatus) {
        showToast(t(`messages.${response?.code}`))
      } else {
        displayErrorToast(t(`messages.${response?.code}`), response?.code)
      }
    } catch (error: any) {
      displayErrorToast(t(`messages.${error?.status}`), error?.status)
    }

    handleCloseModel()
  }

  return {
    activeStep,
    steps,
    appRolesData: appTypesWithRoles?.data,
    roleDetails,
    continueButtonHandler,
    backButtonHandler,
    handleCloseModel,
    addEditRoleHandler,
  }
}
